import React, { useRef, useMemo, useCallback } from 'react'
import { makeStyles } from '@material-ui/core'
import HomeIcon from '@material-ui/icons/Home'
import PropTypes from 'prop-types'
import { useTranslate } from 'react-admin'
import { Field, useFormikContext } from 'formik'
import ImageCropWithEdit from '../../../../ui/ImageCropComponents/ImageCropWithEdit/ImageCropWithEdit'
import GAME_EVENT_FORM_BROADCAST_FIELDS from '../../../../../utils/constants/gameEventFormBroadcastFields'
import { isBroadcastEnded } from '../../../../../utils/helpers/isFieldDisabled'
import emptyOrg from '../../../../../assets/images/org_empty.svg'
import MyTextField from '../../../fields/MyTextField'
import CROPPER_SHAPES from '../../../../../utils/constants/cropperShapes'
import CourtLogosPreviewEditBox from '../../../../CourtLogosPreviewEditBox/CourtLogosPreviewEditBox'
import eventFormCourtLogosStyles from '../../../../EventFormCourtLogos/eventFormCourtLogos.styles'
import useResize from '../../../../../hooks/useResize'
import ORGANISATION_TYPES from '../../../../../utils/constants/OrganisationTypes'
import useGetLocalStorage from '../../../../../hooks/useGetLocalStorage'
import localStorageIds from '../../../../../utils/constants/localStorageIds'
import MyAutocomplete from '../../../fields/MyAutocomplete'
import SelectRenderValue from '../../../../SelectRenderValue/SelectRenderValue'
import isSelectedOrgCompetitionOrFederation from '../../../../../utils/helpers/isSelectedOrgCompetitionOrFederation'

// Radio-like "home team" toggle rendered on the right of each matchup row. The rows never
// reorder: `active` reflects whether this row's index equals `homeTeamIndex`, and clicking
// sets `homeTeamIndex` to this row's index.
const HomeToggle = ({ active, onClick, disabled }) => (
  <button
    type='button'
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    aria-pressed={active}
    aria-label='Set as home team'
    style={{
      // Square matching the filled Autocomplete team-input height (~42px):
      // root padding 10+9 + input padding 6+6 + input height 0.7rem. border-box so the
      // 1.5px border is included in the 42px rather than adding to it.
      boxSizing: 'border-box',
      width: 42,
      height: 42,
      flex: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 6,
      border: `1.5px solid ${active ? '#E06E5C' : '#E2E2E2'}`,
      backgroundColor: active ? '#FCEFEC' : '#FFFFFF',
      cursor: disabled ? 'default' : 'pointer',
      padding: 0,
    }}
  >
    <HomeIcon style={{ fontSize: 22, color: active ? '#E06E5C' : '#B7B7B7' }} />
  </button>
)

HomeToggle.propTypes = {
  active: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
}

const EventTeamsFields = ({ state, orgType, teams, preFilledAway }) => {
  const { values, setFieldValue } = useFormikContext()
  const translate = useTranslate()
  const imageRef = useRef(null)
  const { width } = useResize(imageRef)
  const hasCenterLogo = Boolean(values[GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_CENTER_LOGO])
  const useStyles = useMemo(
    () => makeStyles(eventFormCourtLogosStyles(width, hasCenterLogo)),
    [width, hasCenterLogo],
  )
  const classes = useStyles()
  const localStorageValue = useGetLocalStorage()
  const groupId = localStorageValue?.[localStorageIds.SELECTED_GROUP] || null

  const isClub = orgType === ORGANISATION_TYPES.ORG_CLUB
  const homeTeamIndex = values[GAME_EVENT_FORM_BROADCAST_FIELDS.HOME_TEAM_INDEX]

  // Crest preview matches the 46px input height (design: 46x46 round crest).
  const EDIT_PREVIEW_WIDTH = 46
  const teamLabel = translate('ra.gameDrawer.team')

  const setCroppedImage = useCallback((name, value) => setFieldValue(name, value), [setFieldValue])

  // Non-swapping home model: rows keep their slot; only `homeTeamIndex` moves. A game always
  // has exactly one home team, so clicking the already-active toggle is a no-op — unsetting it
  // submits both teams with isHomeTeam false, which the backend rejects with a 500.
  const handleHomeToggle = useCallback(
    (index) => setFieldValue(GAME_EVENT_FORM_BROADCAST_FIELDS.HOME_TEAM_INDEX, index),
    [setFieldValue],
  )

  const changeTeamField = (e, value, nameField, logoField) => {
    if (isClub) {
      // Row 0 (the only autocomplete for clubs) IS the club-team selection; keep `teams`
      // synced to it so validation (requires teams) and game pairing keep working now that
      // the standalone "Select a team" field is gone.
      setFieldValue('teams', value || null)
    } else {
      const currentTeams = values.teams || []
      if (nameField === GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME)
        setFieldValue('teams', [value, currentTeams[1] || null])
      if (nameField === GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY)
        setFieldValue('teams', [currentTeams[0] || null, value])
    }

    setFieldValue(logoField, value?.logoUrl || value?.teamLogoUrl || null)
    setFieldValue(nameField, value || null)
  }

  const storedHomeTeam = values[GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME]
  const storedAwayTeam = values[GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY]
  const homeTeamId = storedHomeTeam?.teamId ?? storedHomeTeam?.id
  const awayTeamId = storedAwayTeam?.teamId ?? storedAwayTeam?.id

  // Resolve the initial autocomplete value from the options; fall back to the stored team
  // object itself (e.g. a club team keyed by `id` only) so the selection still displays.
  const defaultHomeTeam = useMemo(() => {
    const match = teams.find(
      (team) => homeTeamId != null && (team?.id === homeTeamId || team?.teamId === homeTeamId),
    )
    return match ?? (homeTeamId != null ? storedHomeTeam : undefined)
  }, [teams, homeTeamId, storedHomeTeam])

  const defaultAwayTeam = useMemo(() => {
    const match = teams.find(
      (team) => awayTeamId != null && (team?.id === awayTeamId || team?.teamId === awayTeamId),
    )
    return match ?? (awayTeamId != null ? storedAwayTeam : undefined)
  }, [teams, awayTeamId, storedAwayTeam])

  const isBroadcastEndedMemo = useMemo(() => isBroadcastEnded(state), [state])
  const isCompetitionWithoutGroup = isSelectedOrgCompetitionOrFederation() && !groupId
  const isRowDisabled = useMemo(
    () => isBroadcastEndedMemo || isCompetitionWithoutGroup,
    [isBroadcastEndedMemo, isCompetitionWithoutGroup],
  )

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
        {/* Row 0 (slot 0) — always a team autocomplete (federation + club). */}
        <div className='div-flex' style={{ alignItems: 'flex-start' }}>
          <ImageCropWithEdit
            defaultImageSrc={values[GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME_LOGO]}
            setCroppedImage={(croppedImage) =>
              setCroppedImage(GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME_LOGO, croppedImage)
            }
            editPreviewWidth={EDIT_PREVIEW_WIDTH}
            dialogTitle='Edit Team Logo'
            isDisabled={isRowDisabled}
            emptyPreviewIcon={emptyOrg}
          />
          <Field
            name={GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME}
            defaultValue={defaultHomeTeam}
            label={teamLabel}
            style={{ width: '100%' }}
            required
            disabled={isRowDisabled}
            options={teams?.filter(
              (team) =>
                values[GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY]?.teamId !==
                (team?.teamId || team?.id),
            )}
            component={MyAutocomplete}
            autoSelect={false}
            onChange={(e, value) =>
              changeTeamField(
                e,
                value,
                GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME,
                GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME_LOGO,
              )
            }
            getOptionLabel={(option) => option.name}
            renderOption={(option) => {
              return (
                <SelectRenderValue
                  item={option}
                  chosenOption={values.teams || {}}
                  logoArgName='logoUrl'
                  labelBlack
                />
              )
            }}
          />
          <HomeToggle
            active={homeTeamIndex === 0}
            onClick={() => handleHomeToggle(0)}
            disabled={isRowDisabled}
          />
        </div>

        {/* Row 1 (slot 1) — federation: autocomplete; club: free-text opponent. */}
        <div className='div-flex' style={{ alignItems: 'flex-start' }}>
          <ImageCropWithEdit
            defaultImageSrc={values[GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY_LOGO]}
            setCroppedImage={(croppedImage) =>
              setCroppedImage(GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY_LOGO, croppedImage)
            }
            editPreviewWidth={EDIT_PREVIEW_WIDTH}
            dialogTitle='Edit Team Logo'
            isDisabled={isRowDisabled}
            emptyPreviewIcon={emptyOrg}
          />
          {isClub ? (
            <MyTextField
              name={GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY}
              label={teamLabel}
              required
              disabled={isBroadcastEndedMemo}
              preFilled={preFilledAway}
            />
          ) : (
            <Field
              name={GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY}
              defaultValue={defaultAwayTeam}
              label={teamLabel}
              style={{ width: '100%' }}
              required
              disabled={isRowDisabled}
              options={teams?.filter(
                (team) =>
                  values[GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_HOME]?.teamId !==
                  (team?.teamId || team?.id),
              )}
              autoSelect={false}
              component={MyAutocomplete}
              onChange={(e, value) =>
                changeTeamField(
                  e,
                  value,
                  GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY,
                  GAME_EVENT_FORM_BROADCAST_FIELDS.TEAM_AWAY_LOGO,
                )
              }
              getOptionLabel={(option) => option.name}
              renderOption={(option) => {
                return (
                  <SelectRenderValue
                    item={option}
                    chosenOption={values.teams || {}}
                    logoArgName='logoUrl'
                    labelBlack
                  />
                )
              }}
            />
          )}
          <HomeToggle
            active={homeTeamIndex === 1}
            onClick={() => handleHomeToggle(1)}
            disabled={isRowDisabled}
          />
        </div>

        <div className={classes.competitionFieldsWrapper}>
          <ImageCropWithEdit
            defaultImageSrc={values[GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_LOGO]}
            setCroppedImage={(croppedImage) =>
              setCroppedImage(GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_LOGO, croppedImage)
            }
            aspect={16 / 9}
            minZoom={0.2}
            cropShape={CROPPER_SHAPES.RECT}
            dialogBottomText='Proposed Width: 300-1500 pixels'
            isDisabled={isBroadcastEndedMemo}
            EditPreviewComponent={CourtLogosPreviewEditBox}
            editPreviewComponentClassName={
              values[GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_LOGO]
                ? classes.competitionLogoFilled
                : classes.competitionLogo
            }
          />

          <div style={{ flex: 1 }}>
            <MyTextField
              name={GAME_EVENT_FORM_BROADCAST_FIELDS.COURT_COMPETITION_NAME}
              label='Competition Name'
              isFullWidth
              disabled={isBroadcastEndedMemo}
            />
          </div>
        </div>
      </div>
    </>
  )
}

EventTeamsFields.propTypes = {
  state: PropTypes.string,
  orgType: PropTypes.string,
  teams: PropTypes.array,
  preFilledAway: PropTypes.bool,
}

export default EventTeamsFields
